import { CustomNavLink } from '../../routes';
import { ReactComponent as AccuratorLogo } from '../../../assets/images/aceve/accurator_symbol_white.svg';
import { ReactComponent as ProjectIcon } from '../../../assets/svg/project.svg';
import { ReactComponent as SearchIcon } from '../../../assets/svg/search.svg';
import { ReactComponent as StartIcon } from '../../../assets/svg/start.svg';
import { ReactComponent as InternIcon } from '../../../assets/svg/intern.svg';
import { ReactComponent as ActivityIcon } from '../../../assets/svg/activities.svg';
import { ReactComponent as SupplierIcon } from '../../../assets/svg/suppliers.svg';
import { ReactComponent as OrganizationIcon } from '../../../assets/svg/organization.svg';
import { ReactComponent as ReportsIcon } from '../../../assets/svg/reports.svg';
import { ReactComponent as VideosIcon } from '../../../assets/svg/videos.svg';
import { ReactComponent as MarketingIcon } from '../../../assets/svg/marketingIcon.svg';
import { ReactComponent as NewsIcon } from '../../../assets/svg/news.svg';
import { ReactComponent as BusinessCardIcon } from '../../../assets/svg/businessCard.svg';
import { ReactComponent as FlyerTakenIcon } from '../../../assets/svg/flyerTakenIcon.svg';
import { ReactComponent as MonitorIcon } from '../../../assets/svg/monitor-icon.svg';
import { ReactComponent as FilesIcon } from '../../../assets/svg/files.svg';
import { ReactComponent as RocketIcon } from '../../../assets/svg/rocket_launch.svg';

import RequestQuoteIcon from '../../../components/svgs/requestQuoteIcon';

import appState from '../../../state/AppStateContainer';

import './leftMenuBar.scss';
import NotificationCounter from '../NotificationCounter/NotificationCounter';
import { FC, ReactNode, useCallback, useState, useMemo, useEffect } from 'react';
import { ServiceChat } from '../../../services/service-chat';
import Badge from '../Badge/Badge';
import GlobalSearch from './GlobalSearch';
import { useTranslation } from 'react-i18next';

type MenuItem = {
  title: string,
  to: string,
  icon: ReactNode,
  product?: string,
  notifications?: number,
  counter?: number,
  rightIcon?: ReactNode,
};

const LeftMenuBar : FC = () => {
  const superAdminCheck = appState.isSuperAdmin() && appState.getCurrentOrgSlug() === 'superadmin';
  const { project, internalChat } = appState.getGlobalNotifications();
  const [searchOpen, setSearchOpen] = useState(false);

  const showSearch = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    setSearchOpen(true);
    e.preventDefault();
  }, []);

  //useTranslation(['dashboard', 'help', 'updates',]);
  const [t] = useTranslation('site', { keyPrefix: 'navigation' });

  const supplierApplicationCount = appState.isAdminRole() ? appState.getSupplierApplicationCount() : 0;
  const supplierProfileUpdated = appState.hasSupplierProfileBeenUpdated();
  const isProfileComplete = supplierProfileUpdated;
  const isTeamComplete = appState.isTeamComplete();
  
  const bothTasksComplete = isProfileComplete && isTeamComplete;

  const menuItems: MenuItem[] = useMemo(() => [
    {
      title: t('start.title', 'Start'),
      to: '/dashboard',
      icon: <StartIcon />,
      product: 'PURCHASING',
    },
    {
      title: t('projects.title', 'Projekt'),
      to: '/projects',
      icon: <ProjectIcon />,
      product: 'PURCHASING',
      notifications: project,
    },
    {
      title: t('supplierDatabase.title', 'Leverantörer'),
      to: '/supplier-database',
      icon: <SupplierIcon />,
      counter: supplierApplicationCount,
      product: 'PURCHASING',
    },
    {
      title: t('supplierMonitoring.title', 'Leverantörsbevakning'),
      to: '/supplier-monitoring',
      product: 'PURCHASING',
      icon: <MonitorIcon className='leftMenuBarMonitorIcon' />,
    },
    {
      title: t('files.title', 'Filer'),
      to: '/files',
      product: 'PURCHASING',
      icon: <FilesIcon />,
    },
    {
      title: t('tasks.title', 'Aktiviteter'),
      to: '/tasks',
      icon: <ActivityIcon />,
      product: 'PURCHASING',
    },
    ...(!bothTasksComplete ? [{
      title: t('getStarted.title', 'Kom igång'),
      to: '/get-started',
      icon: <RocketIcon />,
      rightIcon: <Badge style={{ marginLeft: '8px', backgroundColor: 'var(--gray-600)', color: 'var(--white)' }}>
        {t('getStarted.badge.notReady', 'EJ KLAR')}
      </Badge>,
      product: 'SALES',
    }] : []),
    {
      title: t('requests.title', 'Mottagna förfrågningar'),
      to: '/requests',
      icon: <RequestQuoteIcon color={undefined} />,
      product: 'SALES',
    },
    {
      title: t('publishedRequests.title', 'Publicerade förfrågningar'),
      to: '/published-requests',
      icon: <FlyerTakenIcon color={undefined} />,
      product: 'SALES',
    },
    {
      title: t('martketing.title', 'Marknadsföring'),
      to: '/marketing',
      icon: <MarketingIcon color={undefined} className='menu-list-marketing-icon' />,
      product: 'SALES',
    },
    {
      title: t('supplierPresentation.title', 'Presentation'),
      to: '/supplier-presentation',
      icon: <BusinessCardIcon color={undefined} />,
      product: 'SALES',
    },
    {
      title: t('chat.title', 'Användare'),
      to: '/chat',
      icon: <InternIcon />,
      notifications: internalChat,
    },
    {
      title: t('organizations.title', 'Organisationer'),
      to: '/organizations',
      icon: <OrganizationIcon />,
    },
    {
      title: t('reports.title', 'Rapporter'),
      to: '/reports',
      icon: <ReportsIcon />,
    },
    {
      title: t('invoices.title', 'Fakturor'),
      to: '/invoices',
      icon: <ReportsIcon />,
    },
    {
      title: t('help.title', 'Instruktionsfilmer'),
      to: '/help',
      icon: <VideosIcon />,
      product: 'PURCHASING',
    },
    {
      title: t('updates.title', 'Uppdateringar'),
      to: '/updates',
      icon: <NewsIcon />,
    }
  ], [t, supplierApplicationCount, supplierProfileUpdated, project, internalChat, bothTasksComplete]);

  const isClientRole = appState.isClientRole();
  const activeProducts = appState.getActiveProducts();
  const organizationHasSyna = appState.getOrgFromUrl()?.hasSyna;

  const visibleMenuItems = useMemo(() => menuItems.filter(item => {
    if (superAdminCheck && (['/projects', '/tasks', '/requests', '/marketing', '/supplier-presentation', '/published-requests', '/supplier-monitoring', '/files', '/get-started'].includes(item.to))) return false;
    if (isClientRole && (['/supplier-database', '/tasks', '/supplier-monitoring', '/chat'].includes(item.to))) return false;
    if (!superAdminCheck && (['/organizations', '/reports', '/invoices'].includes(item.to))) return false;
    if (!superAdminCheck && item.product && !activeProducts.includes(item.product)) return false;
    if (item.to === '/supplier-monitoring' && !organizationHasSyna) return false;
    return true;
  }), [menuItems, superAdminCheck, isClientRole, activeProducts, organizationHasSyna]);

  useEffect(() => {
    (async () => {
      await appState.refreshProjectNotifications();
    })();
    (async () => {
      const notifications = await ServiceChat.getNotifications();
      const chatNotification = notifications.reduce((a, b) => a + b.count, 0);
      appState.setGlobalNotifications({ internalChat: chatNotification });
    })();
  }, []);

  const os = (navigator.platform.match(/mac|win|linux/i) || ['other'])[0].toLowerCase();
  const shortcut = os == 'mac' ? '⌘+K' : 'Ctrl+K';

  const logoUrl = appState.getLogoUrl();

  return (
    <div className="app-menu-left conv--wrapper">
      <div style={{ backdropFilter: 'blur(10px)', display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%', }}>
        <CustomNavLink to="/dashboard">
          <div className="logo-wrapper">
            {logoUrl ? <img src={logoUrl} style={{ maxWidth: '200px' }} /> : <AccuratorLogo width={40} height={50} />}
          </div>
        </CustomNavLink>

        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', width: '100%' }}>
          <div className='menu-list'>
            {(appState.getActiveProducts().includes('PURCHASING') || appState.isSuperAdmin()) && <>
              <a href="" onClick={showSearch} title={t('search.tooltip', 'Sök bland projekt, leverantörer och sidor och gå direkt dit. {{shortcut}}', { shortcut })}>
                <SearchIcon />
                {t('search.title', 'Sök')}
              </a>
              <GlobalSearch show={searchOpen} setShow={setSearchOpen} />
            </>}
            {visibleMenuItems.map(menu => (
              <CustomNavLink to={menu.to} key={menu.to} activeClassName={'active'}>
                {menu.icon}
                {menu.title}
                {menu.counter ? <NotificationCounter counter={menu.counter} style={{marginLeft: '8px', backgroundColor: 'var(--gray-600)'}} /> : null}
                {menu.notifications ? <NotificationCounter counter={menu.notifications} style={{ marginLeft: '8px', }} /> : null}
                {menu.rightIcon ?? null}
              </CustomNavLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeftMenuBar;
